import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';
import { useEffect, useRef, useState } from 'react';

import { useDismissibleOverlay } from '#/hooks/useDismissibleOverlay';
import { useFocusTrap } from '#/hooks/useFocusTrap';

export type HomepageDesktopTabId = 'productTypes' | 'sectors';

export function useDesktopHomepageNavigation() {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const productButtonRef = useRef<HTMLButtonElement | null>(null);
  const sectorButtonRef = useRef<HTMLButtonElement | null>(null);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [activeTab, setActiveTab] =
    useState<HomepageDesktopTabId>('productTypes');
  const activeTriggerRef =
    activeTab === 'productTypes' ? productButtonRef : sectorButtonRef;
  const { refs, floatingStyles } = useFloating({
    open: overlayOpen,
    placement: activeTab === 'productTypes' ? 'bottom-start' : 'bottom',
    middleware: [offset(12), flip(), shift({ padding: 16 })],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    refs.setReference(activeTriggerRef.current);
  }, [activeTriggerRef, refs]);

  useFocusTrap({
    active: overlayOpen,
    containerRef: overlayRef,
    returnFocusRef: activeTriggerRef,
    allowOutsideClick: true,
  });

  useDismissibleOverlay({
    active: overlayOpen,
    insideRefs: [overlayRef, shellRef],
    onDismiss: () => {
      setOverlayOpen(false);
    },
  });

  const handleTriggerSelect = (tab: HomepageDesktopTabId) => {
    if (overlayOpen && activeTab === tab) {
      setOverlayOpen(false);
      return;
    }

    setActiveTab(tab);
    setOverlayOpen(true);
  };

  const setFloatingRef = (node: HTMLDivElement | null) => {
    overlayRef.current = node;
    refs.setFloating(node);
  };

  return {
    shellRef,
    productButtonRef,
    sectorButtonRef,
    overlayOpen,
    activeTab,
    floatingStyles,
    handleTriggerSelect,
    setFloatingRef,
  };
}
